import { KonvaEventObject } from "konva/lib/Node";
import { Vector2d } from "konva/lib/types";
import React, { useCallback } from "react";
import { Group, Rect } from "react-konva";

import { StrokeWidth } from "components/detector/NotesModal/NotesContent/NotesSection/CustomEditor/ImageUploader/ImageAnnotatorModal/ImageAnnotator/AnnotatorTools/StrokeSettings/StrokeSettings";
import { CanvasAnnotationBoundsUpdate } from "types/imageAnnotations";

import { DEFAULT_MIN_HEIGHT, DEFAULT_MIN_WIDTH } from "../Canvas";
import { BoundingBoxNode, Point } from "../types";
import { calculateCrossSize, clamp } from "../utils";

import { getShadowProps } from "./BoundingBox";
import { CornerNode, EdgeNode, MiddleCross } from "./TransformerElements";

export type HandleNodes = {
  topLeft: Point;
  topMiddle: Point;
  topRight: Point;
  rightMiddle: Point;
  bottomRight: Point;
  bottomMiddle: Point;
  bottomLeft: Point;
  leftMiddle: Point;
};

type TransformerLayerProps = {
  annotationId?: string;
  isSelected: boolean;
  color: string;
  onClick?: (event: KonvaEventObject<MouseEvent>, annotationId: string) => void;
  dimensions: {
    width: number;
    height: number;
  };
  canInteract?: boolean;
  setHoveredNode: (node: BoundingBoxNode) => void;
  handleUpdateBoundingBox: (elementUpdated: CanvasAnnotationBoundsUpdate) => void;

  zoom: number;
  handleNodes: HandleNodes;
  center: Point;
  imageRightBorder: number;
  imageLeftBorder: number;
  imageTopBorder: number;
  imageBottomBorder: number;
  zoomedDimensions: {
    width: number;
    height: number;
  };
  nodeSize: number;
  nodeBorderWidth: number;
  halfNodeBorderWidth: number;
  edgeWidth: number;
  nodeHitbox: number;
  isRotated?: boolean;
  hideMiddleCross?: boolean;
  boxDragBoundFunc?: (pos: Vector2d) => Vector2d;
  showBoxHandle?: boolean;
  minWidth?: number;
  minHeight?: number;
};

function TransformerLayer({
  annotationId,
  isSelected,
  color,
  onClick,
  dimensions,
  canInteract = true,
  setHoveredNode,
  handleUpdateBoundingBox,
  zoom,
  handleNodes,
  center,
  imageRightBorder,
  imageLeftBorder,
  imageTopBorder,
  imageBottomBorder,
  zoomedDimensions,
  nodeSize,
  nodeBorderWidth,
  halfNodeBorderWidth,
  edgeWidth,
  nodeHitbox,
  isRotated = false,
  hideMiddleCross,
  boxDragBoundFunc,
  showBoxHandle,
  minHeight = DEFAULT_MIN_HEIGHT,
  minWidth = DEFAULT_MIN_WIDTH,
}: TransformerLayerProps) {
  // Handle click on the bounding box
  const handleClick = useCallback(
    (event: KonvaEventObject<MouseEvent>) => {
      if (annotationId) onClick?.(event, annotationId);
    },
    [annotationId, onClick]
  );

  return (
    <Group>
      {isSelected && (
        <>
          {/* Box handle */}
          {showBoxHandle && (
            <Rect
              {...getShadowProps(zoom)}
              x={handleNodes.topLeft.x}
              y={handleNodes.topLeft.y}
              width={handleNodes.bottomRight.x - handleNodes.topLeft.x}
              height={handleNodes.bottomRight.y - handleNodes.topLeft.y}
              stroke={color}
              fillEnabled={false}
              draggable
              dragBoundFunc={boxDragBoundFunc}
              strokeWidth={StrokeWidth.THIN / zoom}
              hitStrokeWidth={nodeHitbox}
              onMouseEnter={() => {
                setHoveredNode(isSelected ? "active-bounding-box" : "bounding-box");
              }}
              onMouseLeave={() => {
                setHoveredNode("none");
              }}
              onDragMove={({ target }) => {
                if (!canInteract) {
                  return;
                }
                const x = target.x();
                const y = target.y();

                handleUpdateBoundingBox({
                  left: x,
                  top: y,
                  right: x + dimensions.width,
                  bottom: y + dimensions.height,
                });
              }}
            />
          )}

          {/* Top Middle Handle */}
          <EdgeNode
            hitStrokeWidth={nodeHitbox}
            x={handleNodes.topMiddle.x}
            y={handleNodes.topMiddle.y}
            offsetX={nodeSize / 2}
            offsetY={nodeBorderWidth / 2}
            width={nodeSize}
            height={nodeBorderWidth}
            fill={color}
            nodeType="top-middle"
            setHoveredNode={setHoveredNode}
            dragBoundFunc={(pos) => ({
              x: isRotated ? clamp(pos.x, imageLeftBorder, imageRightBorder) : pos.x,
              y: clamp(pos.y, imageTopBorder, imageBottomBorder),
            })}
            onDragMove={({ target }) => {
              if (!canInteract) {
                return;
              }
              target.x(handleNodes.topMiddle.x);

              // checking if the bounding box is too small and if it's the case, we don't update the bounding box
              // When the bounding box reaches the minimum height, we need to avoid the position of this rect
              // to be updated in the y axis
              if (target.y() > handleNodes.bottomMiddle.y - minHeight) {
                target.y(handleNodes.bottomMiddle.y - minHeight);
              }

              handleUpdateBoundingBox({
                top: target.y(),
              });
            }}
            draggable={canInteract}
          />

          {/* Right Middle Handle */}
          <EdgeNode
            hitStrokeWidth={nodeHitbox}
            x={handleNodes.rightMiddle.x}
            y={handleNodes.rightMiddle.y - nodeSize / 2}
            offsetX={halfNodeBorderWidth}
            width={nodeBorderWidth}
            height={nodeSize}
            fill={color}
            nodeType="right-middle"
            setHoveredNode={setHoveredNode}
            dragBoundFunc={(pos) => ({
              x: clamp(pos.x, imageLeftBorder, imageRightBorder),
              y: isRotated ? clamp(pos.y, imageTopBorder, imageBottomBorder) : pos.y,
            })}
            onDragMove={({ target }) => {
              if (!canInteract) {
                return;
              }

              target.y(handleNodes.rightMiddle.y - nodeSize / 2);

              // checking if the bounding box is too small and if it's the case, we don't update the bounding box
              // When the bounding box reaches the minimum height, we need to avoid the position of this rect
              // to be updated in the x axis
              if (target.x() < handleNodes.leftMiddle.x + minWidth) {
                target.x(handleNodes.leftMiddle.x + minWidth);
              }

              handleUpdateBoundingBox({
                right: target.x(),
              });
            }}
            draggable={canInteract}
          />

          {/* Bottom Middle Handle */}
          <EdgeNode
            hitStrokeWidth={nodeHitbox}
            x={handleNodes.bottomMiddle.x}
            y={handleNodes.bottomMiddle.y}
            offsetX={nodeSize / 2}
            offsetY={nodeBorderWidth / 2}
            width={nodeSize}
            height={nodeBorderWidth}
            fill={color}
            nodeType="bottom-middle"
            setHoveredNode={setHoveredNode}
            dragBoundFunc={(pos) => ({
              x: isRotated ? clamp(pos.x, imageLeftBorder, imageRightBorder) : pos.x,
              y: clamp(pos.y, imageTopBorder, imageBottomBorder),
            })}
            onDragMove={({ target }) => {
              if (!canInteract) {
                return;
              }

              target.x(handleNodes.bottomMiddle.x);

              // checking if the bounding box is too small and if it's the case, we don't update the bounding box
              // When the bounding box reaches the minimum height, we need to avoid the position of this rect
              // to be updated in the y axis
              if (target.y() < handleNodes.topMiddle.y + minHeight) {
                target.y(handleNodes.topMiddle.y + minHeight);
              }

              handleUpdateBoundingBox({
                bottom: target.y(),
              });
            }}
            draggable={canInteract}
          />

          {/* Left Middle Handle */}
          <EdgeNode
            hitStrokeWidth={nodeHitbox}
            x={handleNodes.leftMiddle.x}
            y={handleNodes.leftMiddle.y - nodeSize / 2}
            offsetX={halfNodeBorderWidth}
            width={nodeBorderWidth}
            height={nodeSize}
            fill={color}
            nodeType="left-middle"
            setHoveredNode={setHoveredNode}
            dragBoundFunc={(pos) => ({
              x: clamp(pos.x, imageLeftBorder, imageRightBorder),
              y: isRotated ? clamp(pos.y, imageTopBorder, imageBottomBorder) : pos.y,
            })}
            onDragMove={({ target }) => {
              if (!canInteract) {
                return;
              }

              target.y(handleNodes.leftMiddle.y - nodeSize / 2);

              // Check if the bounding box is too small
              if (target.x() > handleNodes.rightMiddle.x - minWidth) {
                target.x(handleNodes.rightMiddle.x - minWidth);
              }

              handleUpdateBoundingBox({
                left: target.x(),
              });
            }}
            draggable={canInteract}
          />

          {/* Top Left Corner */}
          <CornerNode
            hitStrokeWidth={nodeHitbox}
            setHoveredNode={setHoveredNode}
            x={handleNodes.topLeft.x}
            y={handleNodes.topLeft.y}
            offsetX={halfNodeBorderWidth}
            offsetY={halfNodeBorderWidth}
            borderWidth={nodeBorderWidth}
            size={nodeSize}
            corner="top-left"
            fill={color}
            draggable={canInteract}
            dragBoundFunc={(pos) => ({
              x: clamp(pos.x, imageLeftBorder, imageRightBorder),
              y: clamp(pos.y, imageTopBorder, imageBottomBorder),
            })}
            onDragMove={({ target }) => {
              if (!canInteract) {
                return;
              }

              // checking if the bounding box is too small and if it's the case, we don't update the bounding box
              // When the bounding box reaches the minimum height, we need to avoid the position of this rect
              // to be updated in the y axis. The same applies to the x axis
              if (target.x() > handleNodes.rightMiddle.x - minWidth) {
                target.x(handleNodes.rightMiddle.x - minWidth);
              }

              if (target.y() > handleNodes.bottomMiddle.y - minHeight) {
                target.y(handleNodes.bottomMiddle.y - minHeight);
              }

              handleUpdateBoundingBox({
                left: target.x(),
                top: target.y(),
              });
            }}
          />

          {/* Top Right Corner */}
          <CornerNode
            draggable={canInteract}
            hitStrokeWidth={nodeHitbox}
            setHoveredNode={setHoveredNode}
            x={handleNodes.topRight.x}
            y={handleNodes.topRight.y}
            offsetX={-halfNodeBorderWidth}
            offsetY={halfNodeBorderWidth}
            borderWidth={nodeBorderWidth}
            size={nodeSize}
            corner="top-right"
            fill={color}
            dragBoundFunc={(pos) => ({
              x: clamp(pos.x, imageLeftBorder, imageRightBorder),
              y: clamp(pos.y, imageTopBorder, imageBottomBorder),
            })}
            onDragMove={({ target }) => {
              if (!canInteract) {
                return;
              }

              // checking if the bounding box is too small and if it's the case, we don't update the bounding box
              // When the bounding box reaches the minimum height, we need to avoid the position of this rect
              // to be updated in the y axis. The same applies to the x axis
              if (target.x() < handleNodes.leftMiddle.x + minWidth) {
                target.x(handleNodes.leftMiddle.x + minWidth);
              }

              if (target.y() > handleNodes.bottomMiddle.y - minHeight) {
                target.y(handleNodes.bottomMiddle.y - minHeight);
              }

              handleUpdateBoundingBox({
                right: target.x(),
                top: target.y(),
              });
            }}
          />

          {/* Bottom Right Corner */}
          <CornerNode
            draggable={canInteract}
            hitStrokeWidth={nodeHitbox}
            setHoveredNode={setHoveredNode}
            x={handleNodes.bottomRight.x}
            y={handleNodes.bottomRight.y}
            offsetX={-halfNodeBorderWidth}
            offsetY={-halfNodeBorderWidth}
            borderWidth={nodeBorderWidth}
            size={nodeSize}
            corner="bottom-right"
            fill={color}
            dragBoundFunc={(pos) => ({
              x: clamp(pos.x, imageLeftBorder, imageRightBorder),
              y: clamp(pos.y, imageTopBorder, imageBottomBorder),
            })}
            onDragMove={({ target }) => {
              if (!canInteract) {
                return;
              }

              // checking if the bounding box is too small and if it's the case, we don't update the bounding box
              // When the bounding box reaches the minimum height, we need to avoid the position of this rect
              // to be updated in the y axis. The same applies to the x axis
              if (target.y() < handleNodes.topMiddle.y + minHeight) {
                target.y(handleNodes.topMiddle.y + minHeight);
              }

              if (target.x() < handleNodes.leftMiddle.x + minWidth) {
                target.x(handleNodes.leftMiddle.x + minWidth);
              }

              handleUpdateBoundingBox({
                bottom: target.y(),
                right: target.x(),
              });
            }}
          />

          {/* Bottom Left Corner */}
          <CornerNode
            draggable={canInteract}
            hitStrokeWidth={nodeHitbox}
            setHoveredNode={setHoveredNode}
            x={handleNodes.bottomLeft.x}
            y={handleNodes.bottomLeft.y}
            offsetX={halfNodeBorderWidth}
            offsetY={-halfNodeBorderWidth}
            borderWidth={nodeBorderWidth}
            size={nodeSize}
            corner="bottom-left"
            fill={color}
            dragBoundFunc={(pos) => ({
              x: clamp(pos.x, imageLeftBorder, imageRightBorder),
              y: clamp(pos.y, imageTopBorder, imageBottomBorder),
            })}
            onDragMove={({ target }) => {
              if (!canInteract) {
                return;
              }
              // checking if the bounding box is too small and if it's the case, we don't update the bounding box
              // When the bounding box reaches the minimum height, we need to avoid the position of this rect
              // to be updated in the y axis. The same applies to the x axis
              if (target.x() > handleNodes.rightMiddle.x - minWidth) {
                target.x(handleNodes.rightMiddle.x - minWidth);
              }
              // Check if the bounding box is too small
              if (target.y() < handleNodes.topMiddle.y + minHeight) {
                target.y(handleNodes.topMiddle.y + minHeight);
              }

              handleUpdateBoundingBox({
                left: target.x(),
                bottom: target.y(),
              });
            }}
          />
        </>
      )}

      {/* Middle Cross */}
      {!hideMiddleCross && (
        <MiddleCross
          draggable={canInteract}
          onMouseDown={handleClick}
          color={color}
          center={center}
          borderWidth={edgeWidth}
          size={calculateCrossSize(dimensions.width, dimensions.height, zoom)}
          dragBoundFunc={(pos) => {
            const calculateX = () =>
              clamp(
                pos.x,
                imageLeftBorder + zoomedDimensions.height / 2 - halfNodeBorderWidth,
                imageRightBorder - zoomedDimensions.height / 2
              );

            const calculateY = () =>
              clamp(
                pos.y,
                imageTopBorder + zoomedDimensions.width / 2,
                imageBottomBorder - zoomedDimensions.width / 2 - halfNodeBorderWidth
              );
            return {
              x: isRotated
                ? calculateX()
                : clamp(
                    pos.x,
                    imageLeftBorder + zoomedDimensions.width / 2,
                    imageRightBorder - zoomedDimensions.width / 2
                  ),
              y: isRotated
                ? calculateY()
                : clamp(
                    pos.y,
                    imageTopBorder + zoomedDimensions.height / 2,
                    imageBottomBorder - zoomedDimensions.height / 2
                  ),
            };
          }}
          onDragMove={({ target }) => {
            if (!canInteract) {
              return;
            }

            const x = target.x() - dimensions.width / 2;
            const y = target.y() - dimensions.height / 2;

            handleUpdateBoundingBox({
              left: x,
              top: y,
              right: x + dimensions.width,
              bottom: y + dimensions.height,
            });
          }}
          hitStrokeWidth={nodeHitbox}
          onMouseEnter={() => {
            setHoveredNode(isSelected ? "active-bounding-box" : "bounding-box");
          }}
          onMouseLeave={() => {
            setHoveredNode("none");
          }}
        />
      )}
    </Group>
  );
}

export default React.memo(TransformerLayer);
